import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Set up axios interceptor
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Load user on startup
  useEffect(() => {
    const loadUser = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
        } catch (error) {
          console.error('Failed to load user:', error);
          logout();
        }
      }
      setLoading(false);
    };

    loadUser();
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      });
      
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      
      const { access_token, user: newUser } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(newUser);
      
      return { success: true };
    } catch (error) {
      console.error('Registration error:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const updateUser = async (updateData) => {
    try {
      const response = await axios.put(`${API}/auth/me`, updateData);
      setUser(response.data);
      return { success: true };
    } catch (error) {
      console.error('Update user error:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Update failed' 
      };
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateUser,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// API Service functions
export const apiService = {
  // Categories
  getCategories: async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      return response.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      return [];
    }
  },

  // Listings
  getListings: async (params = {}) => {
    try {
      const response = await axios.get(`${API}/listings`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching listings:', error);
      return { items: [], total: 0, page: 1, limit: 20, pages: 0 };
    }
  },

  // Recherche avec autocomplétion
  searchListings: async (query, options = {}) => {
    try {
      const params = {
        q: query,
        limit: options.limit || 20,
        preview: options.preview || false,
        ...options
      };
      const response = await axios.get(`${API}/listings/search`, { params });
      return { success: true, data: response.data.items || response.data };
    } catch (error) {
      console.error('Error searching listings:', error);
      return { success: false, data: [] };
    }
  },

  getListing: async (listingId) => {
    try {
      const response = await axios.get(`${API}/listings/${listingId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching listing:', error);
      return null;
    }
  },

  createListing: async (listingData) => {
    try {
      const response = await axios.post(`${API}/listings`, listingData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error creating listing:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Failed to create listing' 
      };
    }
  },

  updateListing: async (listingId, updateData) => {
    try {
      const response = await axios.put(`${API}/listings/${listingId}`, updateData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error updating listing:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Failed to update listing' 
      };
    }
  },

  deleteListing: async (listingId) => {
    try {
      await axios.delete(`${API}/listings/${listingId}`);
      return { success: true };
    } catch (error) {
      console.error('Error deleting listing:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Failed to delete listing' 
      };
    }
  },

  // Favorites
  addToFavorites: async (listingId) => {
    try {
      await axios.post(`${API}/listings/${listingId}/favorite`);
      return { success: true };
    } catch (error) {
      console.error('Error adding to favorites:', error);
      return { success: false };
    }
  },

  removeFromFavorites: async (listingId) => {
    try {
      await axios.delete(`${API}/listings/${listingId}/favorite`);
      return { success: true };
    } catch (error) {
      console.error('Error removing from favorites:', error);
      return { success: false };
    }
  },

  getUserFavorites: async () => {
    try {
      const response = await axios.get(`${API}/favorites`);
      return response.data;
    } catch (error) {
      console.error('Error fetching favorites:', error);
      return [];
    }
  },

  // Conversations
  getConversations: async () => {
    try {
      const response = await axios.get(`${API}/conversations`);
      return response.data;
    } catch (error) {
      console.error('Error fetching conversations:', error);
      return [];
    }
  },

  createConversation: async (listingId) => {
    try {
      const response = await axios.post(`${API}/conversations`, null, {
        params: { listing_id: listingId }
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error creating conversation:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Failed to create conversation' 
      };
    }
  },

  getConversationMessages: async (conversationId, limit = 50, offset = 0) => {
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}/messages`, {
        params: { limit, offset }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching messages:', error);
      return [];
    }
  },

  sendMessage: async (conversationId, content, messageType = 'text') => {
    try {
      const response = await axios.post(`${API}/conversations/${conversationId}/messages`, {
        content,
        message_type: messageType,
        conversation_id: conversationId
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error sending message:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Failed to send message' 
      };
    }
  },

  deleteMessage: async (messageId) => {
    try {
      const response = await axios.delete(`${API}/messages/${messageId}`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error deleting message:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Failed to delete message' 
      };
    }
  },

  contactSeller: async (listingId) => {
    try {
      const response = await axios.post(`${API}/listings/${listingId}/contact`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error contacting seller:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Failed to contact seller' 
      };
    }
  },

  markConversationRead: async (conversationId) => {
    try {
      const response = await axios.post(`${API}/conversations/${conversationId}/mark-read`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error marking conversation as read:', error);
      return { success: false };
    }
  },

  // Additional Listing methods
  getUserListings: async (userId, status = null) => {
    try {
      const params = status ? { status } : {};
      const response = await axios.get(`${API}/users/${userId}/listings`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching user listings:', error);
      return [];
    }
  },

  getMyListings: async (status = null) => {
    try {
      const params = status ? { status } : {};
      const response = await axios.get(`${API}/my/listings`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching my listings:', error);
      return [];
    }
  },

  getListingsByCategory: async (categoryId, limit = 10) => {
    try {
      const response = await axios.get(`${API}/categories/${categoryId}/listings`, {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching listings by category:', error);
      return [];
    }
  },

  getSimilarListings: async (listingId, limit = 5) => {
    try {
      const response = await axios.get(`${API}/listings/${listingId}/similar`, {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching similar listings:', error);
      return [];
    }
  },

  getFeaturedListings: async (limit = 10) => {
    try {
      const response = await axios.get(`${API}/featured-listings`, {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching featured listings:', error);
      return [];
    }
  },

  searchListings: async (query, limit = 20) => {
    try {
      const response = await axios.get(`${API}/search/listings`, {
        params: { q: query, limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching listings:', error);
      return [];
    }
  },

  updateListingStatus: async (listingId, status) => {
    try {
      const response = await axios.patch(`${API}/listings/${listingId}/status`, {
        status
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error updating listing status:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Failed to update listing status' 
      };
    }
  },

  restoreListing: async (listingId) => {
    try {
      const response = await axios.patch(`${API}/listings/${listingId}/restore`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error restoring listing:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Failed to restore listing' 
      };
    }
  },

  getListingStatistics: async () => {
    try {
      const response = await axios.get(`${API}/statistics/listings`);
      return response.data;
    } catch (error) {
      console.error('Error fetching listing statistics:', error);
      return null;
    }
  },

  getTrendingCategories: async (days = 7) => {
    try {
      const response = await axios.get(`${API}/trending/categories`, {
        params: { days }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching trending categories:', error);
      return [];
    }
  },

  bulkUpdateListings: async (listingIds, updateData) => {
    try {
      const response = await axios.patch(`${API}/bulk/listings/update`, {
        listing_ids: listingIds,
        update_data: updateData
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error bulk updating listings:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Failed to bulk update listings' 
      };
    }
  },

  // User Profile methods
  getUserStats: async (userId) => {
    try {
      const response = await axios.get(`${API}/users/${userId}/stats`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user stats:', error);
      return {
        listingsCount: 0,
        favoritesCount: 0,
        rating: 5.0,
        reviewsCount: 0
      };
    }
  },

  updateProfile: async (profileData) => {
    try {
      const response = await axios.put(`${API}/auth/me`, profileData);
      return response.data;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  },

  getListingById: async (listingId) => {
    try {
      const response = await axios.get(`${API}/listings/${listingId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching listing by ID:', error);
      throw error;
    }
  },

  incrementListingViews: async (listingId) => {
    try {
      await axios.post(`${API}/listings/${listingId}/view`);
      return { success: true };
    } catch (error) {
      console.error('Error incrementing listing views:', error);
      return { success: false };
    }
  },
};
